import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, AlertCircle, ShieldAlert } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUpdateSkill, useSkillRollbackEvents, type SkillRegistryRow } from "@/hooks/useSkillDetail";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

// Onda 9a: rollout binário — canary removido. Estados aceitos: off | shadow | on.
const STATES = ["off", "shadow", "on"] as const;
type RolloutState = (typeof STATES)[number];

interface Props {
  skill: SkillRegistryRow;
}

export function RolloutTab({ skill }: Props) {
  const { toast } = useToast();
  const update = useUpdateSkill();
  const events = useSkillRollbackEvents(skill.id);

  const cfg = (skill.auto_rollback_config ?? {}) as Record<string, number>;
  const [negFb, setNegFb] = useState(cfg.negative_feedback_rate_24h ?? 0.05);
  const [vRej, setVRej] = useState(cfg.verifier_rejection_rate_24h ?? 0.1);
  const [minExec, setMinExec] = useState(cfg.min_executions_24h ?? 20);

  const recentRollback = events.data?.[0];
  const rollbackRecent =
    recentRollback &&
    Date.now() - new Date(recentRollback.created_at).getTime() < 60 * 60_000;

  const setState = (rollout_state: RolloutState) => {
    update.mutate(
      { id: skill.id, patch: { rollout_state } },
      {
        onSuccess: () => toast({ title: `Estado alterado para ${rollout_state}` }),
        onError: (e) =>
          toast({
            title: "Bloqueado pelo gate",
            description: e.message,
            variant: "destructive",
          }),
      },
    );
  };

  const saveAutoRollback = () => {
    update.mutate(
      {
        id: skill.id,
        patch: {
          auto_rollback_config: {
            negative_feedback_rate_24h: negFb,
            verifier_rejection_rate_24h: vRej,
            min_executions_24h: minExec,
          },
        },
      },
      { onSuccess: () => toast({ title: "Auto-rollback config salva" }) },
    );
  };

  return (
    <div className="space-y-4">
      {rollbackRecent && (
        <div className="border border-destructive/40 bg-destructive/10 rounded-md p-3 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-destructive mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-destructive">Auto-rollback disparado</div>
            <div className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(recentRollback!.created_at), { addSuffix: true, locale: ptBR })} ·{" "}
              {recentRollback!.reason}
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estado de rollout</CardTitle>
          <CardDescription>
            Atual: <Badge>{skill.rollout_state}</Badge>. Promover para <code>on</code> exige golden run válido
            (8+ casos, 2 adversariais, pass_rate ≥ 0.8).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {STATES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={skill.rollout_state === s ? "default" : "outline"}
                onClick={() => setState(s)}
                disabled={update.isPending}
              >
                {s}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auto-rollback</CardTitle>
          <CardDescription>
            O cron <code>skill-auto-rollback-cron</code> roda a cada 5 min e força para shadow se exceder estes limites.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Negative feedback rate (24h)</label>
            <Input
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={negFb}
              onChange={(e) => setNegFb(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Verifier rejection rate (24h)</label>
            <Input
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={vRej}
              onChange={(e) => setVRej(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Mín. execuções (24h)</label>
            <Input
              type="number"
              min={1}
              value={minExec}
              onChange={(e) => setMinExec(Number(e.target.value))}
            />
          </div>
          <div className="md:col-span-3">
            <Button size="sm" onClick={saveAutoRollback} disabled={update.isPending}>
              {update.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Salvar auto-rollback
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Histórico de rollbacks
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.isLoading ? (
            <div className="text-xs text-muted-foreground">Carregando…</div>
          ) : (events.data?.length ?? 0) === 0 ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Nenhum rollback registrado.</div>
          ) : (
            <div className="space-y-2">
              {events.data!.map((ev) => (
                <div key={ev.id} className="border rounded p-2 text-xs space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="text-[10px]">{ev.from_state} → {ev.to_state}</Badge>
                    <span className="text-muted-foreground">
                      {formatDistanceToNow(new Date(ev.created_at), { addSuffix: true, locale: ptBR })}
                    </span>
                  </div>
                  <div>{ev.reason}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
